/**
 * 官网数据层 —— 只读 ERP 的 /public/catalog 快照。
 *
 * 为什么整站只发一个请求：
 *   3 级结构（品牌→型号→颜色）数据量很小（线上 7 品牌 / 156 型号 / 382 图，约 72KB），
 *   一次取回后在内存里查，翻页零请求、无白屏、无 loading 抖动。
 *
 * 为什么要重试：
 *   实测跨境链路对较大响应会**间歇性截断**（HTTP 仍 200，但 JSON 不完整），
 *   前端不重试就只剩一个空白页。失败自动重试一次，再失败才报错。
 */
import type { SiteConfig } from '@bosamie/shared';

export interface CatalogColor {
  id: string;
  imageUrl: string | null;
}

export interface CatalogModel {
  id: string;
  code: string;
  name: string;
  price: number;
  coverUrl: string | null;
  colorCount: number;
  colors: CatalogColor[];
}

export interface CatalogBrand {
  id: string;
  name: string;
  coverUrl: string | null;
  modelCount: number;
  models: CatalogModel[];
}

export interface CatalogBanner {
  id: string;
  imageUrl: string;
  link: string | null;
}

export interface Catalog {
  version: string;
  generatedAt: string;
  site: SiteConfig;
  banners: CatalogBanner[];
  brands: CatalogBrand[];
}

const CATALOG_URL = '/api/catalog';
const MAX_ATTEMPTS = 2;

let cached: Catalog | null = null;
let inflight: Promise<Catalog> | null = null;

async function fetchCatalog(): Promise<Catalog> {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      const res = await fetch(CATALOG_URL, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as Catalog;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Failed to load catalog');
}

/** 取目录；同一时刻的并发调用共享同一个请求。 */
export function loadCatalog(force = false): Promise<Catalog> {
  if (!force && cached) return Promise.resolve(cached);
  if (!force && inflight) return inflight;

  inflight = fetchCatalog()
    .then((data) => {
      cached = data;
      inflight = null;
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** 供测试或手动刷新使用：清掉内存缓存。 */
export function clearCatalogCache(): void {
  cached = null;
  inflight = null;
}
